import React from "react";
import { AlertCircle } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  RUBRIQUE_CODES,
  getAllRubriqueCodes,
  getCategoryDisplayName,
} from "@/data/silae-rubriques";
import type { RubriqueSelectProps } from "@/types/silae";
import { SquarePi } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * Composant réutilisable pour la sélection de rubrique SILAE
 * Permet la sélection dans une liste organisée ou la saisie manuelle
 */
export const RubriqueSelect: React.FC<RubriqueSelectProps> = ({
  value,
  onValueChange,
  onLabelAutoFill,
  isManualEntry,
  onManualEntryChange,
  placeholder,
  error,
  touched,
  isElse = false,
  onBlur,
}) => {
  const { t } = useTranslation();

  const defaultPlaceholder = placeholder || t("rubriqueSelect.placeholder");
  const themeColors = isElse
    ? {
        bg: "bg-orange-50 dark:bg-orange-800/20 border border-orange-200 dark:border-orange-800",
        text: "text-orange-900 dark:text-orange-100",
        textMuted: "text-orange-700 dark:text-orange-300",
        icon: "text-orange-900 dark:text-orange-50",
        accent: "text-orange-600 dark:text-orange-400",
      }
    : {
        bg: "bg-sky-50 dark:bg-sky-800/20 border border-sky-200 dark:border-sky-800",
        text: "text-sky-900 dark:text-sky-100",
        textMuted: "text-sky-700 dark:text-sky-300",
        icon: "text-sky-900 dark:text-sky-50",
        accent: "text-sky-600 dark:text-sky-400",
      };

  const handleValueChange = (selectedValue: string) => {
    if (selectedValue === "MANUAL_ENTRY") {
      onManualEntryChange(true);
      onValueChange("");
    } else {
      onValueChange(selectedValue);
      // Obtenir les détails du code sélectionné pour le pré-remplissage
      const selectedCode = getAllRubriqueCodes().find(
        (c) => c.code === selectedValue,
      );
      if (selectedCode && onLabelAutoFill) {
        onLabelAutoFill(selectedCode.label);
      }
    }
  };

  const handleManualToList = () => {
    onManualEntryChange(false);
    onValueChange("");
  };

  const selectedCode =
    !isManualEntry && value
      ? getAllRubriqueCodes().find((c) => c.code === value)
      : null;

  return (
    <div className="space-y-2">
      {!isManualEntry ? (
        // Mode sélection avec dropdown
        <Select
          value={value === "MANUAL_ENTRY" ? "" : value}
          onValueChange={handleValueChange}
        >
          <SelectTrigger className={touched && error ? "border-red-500" : ""}>
            <SelectValue placeholder={defaultPlaceholder} />
          </SelectTrigger>
          <SelectContent className="max-h-96 overflow-y-auto">
            {/* Codes organisés par catégorie */}
            {Object.entries(RUBRIQUE_CODES).map(([categoryKey, codes]) => (
              <div key={categoryKey}>
                <div className="px-2 py-1.5 text-sm font-medium text-muted-foreground bg-muted/30  top-0">
                  {getCategoryDisplayName(categoryKey)}
                </div>
                {codes.map((code) => (
                  <SelectItem
                    key={code.code}
                    value={code.code}
                    className="py-2"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-foreground">
                        {code.code}
                      </span>
                      <span className="text-muted-foreground">-</span>
                      <span className="flex-1">{code.label}</span>
                    </div>
                  </SelectItem>
                ))}
              </div>
            ))}

            {/* Option saisie manuelle */}
            <div className="h-px bg-border my-2"></div>
            <SelectItem value="MANUAL_ENTRY" className="py-3">
              <div className="flex items-center gap-2">
                <span className={`font-medium ${themeColors.accent}`}>
                  {t("rubriqueSelect.manualEntry.label")}
                </span>
                <span className="text-muted-foreground">-</span>
                <span className="flex-1">
                  {t("rubriqueSelect.manualEntry.description")}
                </span>
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
      ) : (
        // Mode saisie manuelle
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Input
              placeholder={t("rubriqueSelect.manualEntry.placeholder")}
              value={value}
              onChange={(e) => onValueChange(e.target.value.toUpperCase())}
              onBlur={() => {
                // Déclencher setFieldTouched pour Formik
                if (onBlur) {
                  const fieldName = isElse
                    ? "elseRubriqueNumber"
                    : "rubriqueNumber";
                  onBlur(fieldName);
                }
              }}
              className={`${touched && error ? "border-red-500 focus:border-red-500" : ""}`}
              aria-invalid={touched && error ? "true" : "false"}
              aria-describedby={error ? "rubrique-error" : undefined}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleManualToList}
            >
              {t("rubriqueSelect.manualEntry.backToList")}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            💡 {t("rubriqueSelect.manualEntry.helpText")}
          </p>
        </div>
      )}

      {/* Affichage des erreurs */}
      {touched && error && (
        <p
          id="rubrique-error"
          className="text-sm text-red-500 flex items-center gap-1"
        >
          <AlertCircle className="h-3 w-3" />
          {error}
        </p>
      )}

      {/* Affichage des propriétés du code sélectionné (seulement pour codes prédéfinis) */}
      {selectedCode && (
        <div className={`p-3 border rounded-lg ${themeColors.bg}`}>
          <div className="flex items-center gap-2 mb-2">
            <SquarePi className={`h-4 w-4 ${themeColors.icon}`} />
            <span className={`text-sm font-medium ${themeColors.text}`}>
              {selectedCode.code} - {selectedCode.label}
            </span>
          </div>
          <p className={`text-sm ${themeColors.textMuted} mb-2`}>
            {selectedCode.description}
          </p>
          <div className="flex flex-wrap gap-2 text-xs">
            <Badge
              variant={
                selectedCode.properties.imposable ? "destructive" : "secondary"
              }
            >
              {selectedCode.properties.imposable
                ? t("rubriqueSelect.properties.imposable")
                : t("rubriqueSelect.properties.nonImposable")}
            </Badge>
            <Badge
              variant={
                selectedCode.properties.soumis_cotisations
                  ? "destructive"
                  : "secondary"
              }
            >
              {selectedCode.properties.soumis_cotisations
                ? t("rubriqueSelect.properties.soumisCotisations")
                : t("rubriqueSelect.properties.exonereCotisations")}
            </Badge>
            <Badge
              variant={selectedCode.properties.base_cp ? "default" : "outline"}
            >
              {selectedCode.properties.base_cp
                ? t("rubriqueSelect.properties.inclusBaseCp")
                : t("rubriqueSelect.properties.excluBaseCp")}
            </Badge>
          </div>
        </div>
      )}

      {/* Message informatif pour saisie manuelle */}
      {isManualEntry && value && (
        <div className={`p-3 border rounded-lg ${themeColors.bg}`}>
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className={`h-4 w-4 ${themeColors.icon}`} />
            <span className={`text-sm font-medium ${themeColors.text}`}>
              {isElse
                ? t("rubriqueSelect.customCode.titleElse")
                : t("rubriqueSelect.customCode.title")}
              : {value}
            </span>
          </div>
          <p className={`text-sm ${themeColors.textMuted}`}>
            {t("rubriqueSelect.customCode.description")}
          </p>
        </div>
      )}
    </div>
  );
};
